/**
 * Shared "kick off Optro sign-in" sequence — bind this browser to a fresh
 * attempt and hand off to the backend at `baseUrl`.
 *
 * Used by both the manual "Sign in with Optro" dialog and the referrer-based
 * auto sign-in, so the two can't drift out of sync on the binding + cookie +
 * navigation sequence.
 */
import { config } from "@/config";
import { createOptroStateBinding } from "./optroStateBinding";

/**
 * Binds this browser to a fresh Optro sign-in and navigates to the backend
 * kickoff.
 *
 * Clears Google Sign-In's non-RFC-6265-compliant `g_state` cookie on
 * localhost first — it leaks across ports to the AB backend there and 400s
 * the authorize endpoint. Different domains in prod mean it can't leak
 * there.
 *
 * Rejects when `crypto.subtle` is unavailable (a non-secure-context http
 * origin that isn't localhost) — callers decide how to surface that.
 */
export async function startOptroLogin(baseUrl: string): Promise<void> {
  const stateBinding = await createOptroStateBinding();
  if (window.location.hostname === "localhost") {
    document.cookie = "g_state=; Max-Age=0; path=/";
  }
  window.location.href = `${config.apiUrl}/auth/optrooauth/login?optro_base_url=${encodeURIComponent(
    baseUrl,
  )}&state_binding=${encodeURIComponent(stateBinding)}`;
}
